import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicSite } from "@/lib/publicSite";

const Ctx = createContext({ loading: true, site: null, error: null });

/**
 * Loads the org's published CMS bundle once per :orgSlug route param.
 *
 * Data shape:
 *   {
 *     org:           { id, slug, name, display_name, logo_url,
 *                      brand_primary_hex, brand_accent_hex,
 *                      contact_email, contact_phone, contact_address,
 *                      enrollment_open },
 *     pages:         [{ id, slug, title, body_md, updated_at }, …],
 *     announcements: [{ id, title, body_md, published_at }, …],
 *   }
 *
 * `brandCss` is a ready-to-spread style object for the outer site wrapper
 * (sets `--color-primary` / `--color-accent` from the org's branding).
 */
export function PublicSiteProvider({ children }) {
  const { orgSlug } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setSite(null);
    (async () => {
      try {
        const s = await getPublicSite(orgSlug);
        if (!alive) return;
        setSite(s);
      } catch (err) {
        if (!alive) return;
        setError(err?.message || "Failed to load site");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgSlug]);

  const brandCss = useMemo(() => {
    const org = site?.org;
    return {
      ["--color-primary"]: org?.brand_primary_hex || "#5A7E46",
      ["--color-accent"]:  org?.brand_accent_hex  || "#C6851E",
    };
  }, [site]);

  const value = useMemo(
    () => ({ loading, site, error, orgSlug, brandCss }),
    [loading, site, error, orgSlug, brandCss],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePublicSite() {
  return useContext(Ctx);
}
